/** The only literal color forms we accept: hex (3/4/6/8 digits) and `rgb()` / `rgba()`. */
const HEX_COLOR = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

/**
 * Full `rgb()` / `rgba()` grammar — three components plus an optional alpha, in the legacy comma
 * or the modern space/slash form.
 */
const RGB_COLOR = /^rgba?\(\s*\d*\.?\d+%?(?:\s*(?:,|\s)\s*\d*\.?\d+%?){2}(?:\s*[,/]\s*\d*\.?\d+%?)?\s*\)$/i;

/** True only for a literal color value: a hex color or an `rgb()` / `rgba()` notation. */
export const isCssColor = (value: string): boolean => {
    const normalized = value.trim();
    return HEX_COLOR.test(normalized) || RGB_COLOR.test(normalized);
};

/**
 * Resolves any valid CSS color expression (e.g. named color, HSL, OKLCH, variable, or light-dark())
 * into a computed CSS rgb/rgba string by using a browser-native element probe.
 * Explicitly sets the `colorScheme` on the probe element to match the requested theme so that `light-dark()`
 * functions evaluate correctly during render time, independent of HTML attribute updates.
 */
export const computeCssColor = (colorVar: string, theme?: string): string => {
    if (typeof window === 'undefined' || !colorVar) return '';
    const cssValue = colorVar.startsWith('var(') || !colorVar.startsWith('--') ? colorVar : `var(${colorVar})`;

    const tempEl = document.createElement('span');
    tempEl.style.color = cssValue;
    if (theme) {
        tempEl.style.colorScheme = theme; // Force resolution context for light-dark()
    }
    tempEl.style.display = 'none';
    document.body.appendChild(tempEl);

    const resolvedColor = window.getComputedStyle(tempEl).color;
    document.body.removeChild(tempEl);

    return resolvedColor;
};

/**
 * Converts a hex string or rgb/rgba string into Lottie's normalized [r, g, b] triple (0..1).
 * Accepts any CSS color using the browser compute engine.
 */
export const parseToNormalizedRgb = (color: string, theme?: string): [number, number, number] | null => {
    const trimmed = color.trim();
    const lowerTrimmed = trimmed.toLowerCase();

    // 1. Direct Hex colors (#fff, #ffffff, #fffaa, #ffffffaa)
    if (HEX_COLOR.test(lowerTrimmed)) {
        const hex = lowerTrimmed.replace('#', '');
        let r = 0,
            g = 0,
            b = 0;

        if (hex.length === 3 || hex.length === 4) {
            r = parseInt(hex[0] + hex[0], 16);
            g = parseInt(hex[1] + hex[1], 16);
            b = parseInt(hex[2] + hex[2], 16);
        } else if (hex.length >= 6) {
            r = parseInt(hex.slice(0, 2), 16);
            g = parseInt(hex.slice(2, 4), 16);
            b = parseInt(hex.slice(4, 6), 16);
        }
        return [r / 255, g / 255, b / 255];
    }

    // 2. Direct rgb/rgba colors (accepts comma-separated and space/slash-separated)
    if (RGB_COLOR.test(lowerTrimmed)) {
        // Strip out the "rgb(", "rgba(", and ")" parts
        const valuesOnly = lowerTrimmed
            .replace(/^rgba?\(/i, '')
            .replace(/\)$/, '')
            .trim();

        // Split components by comma, slash or space
        const parts = valuesOnly.split(/[\s,/]+/g).filter(Boolean);

        if (parts.length >= 3) {
            const r = parseInt(parts[0], 10);
            const g = parseInt(parts[1], 10);
            const b = parseInt(parts[2], 10);
            return [r / 255, g / 255, b / 255];
        }
    }

    // 3. Complex formats (named, HSL, CSS variables, etc.) resolved via computed style with explicit theme context
    const resolved = computeCssColor(trimmed, theme);
    if (resolved && resolved !== color) {
        return parseToNormalizedRgb(resolved, theme);
    }

    return null;
};
